import React, { useState } from "react";
import { apiFetch } from "../func/auth";
import { getDiscordAvatarUrl } from "../func/utils";

function AccessRequestPanel({
    apiToken,
    profile,
    onProfileChange,
    onSignInAgain,
    onLogout,
}) {
    const [requestText, setRequestText] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const request = profile?.request;
    const user = profile?.user;
    const accessApproved = Boolean(
        profile?.requires_reauthentication || request?.status === "approved"
    );

    async function submitRequest(event) {
        event.preventDefault();
        const message = requestText.trim();
        if (!message || loading) return;

        setLoading(true);
        setError("");
        try {
            const payload = await apiFetch("/api/auth/access-requests", {
                token: apiToken,
                method: "POST",
                body: { request_text: message },
            });
            onProfileChange({ ...profile, request: payload.request });
            setRequestText("");
        } catch (requestError) {
            setError(requestError.message);
        } finally {
            setLoading(false);
        }
    }

    async function checkStatus() {
        if (loading) return;

        setLoading(true);
        setError("");
        try {
            const payload = await apiFetch("/api/auth/me", { token: apiToken });
            if (payload.authorized) {
                onSignInAgain();
                return;
            }
            onProfileChange({
                ...profile,
                authorized: false,
                is_admin: Boolean(payload.is_admin),
                user: payload.user || user,
                request: payload.request || null,
                token_type: payload.token_type || profile?.token_type || null,
                requires_reauthentication: false,
            });
        } catch (requestError) {
            setError(requestError.message);
        } finally {
            setLoading(false);
        }
    }

    const canSubmit = !accessApproved && (!request || request.status === "denied");

    return (
        <div className="min-h-dvh bg-slate-50 px-4 py-8 text-slate-900 dark:bg-slate-900 dark:text-white sm:px-6">
            <main className="mx-auto grid min-h-[calc(100dvh-4rem)] w-full max-w-xl place-items-center">
                <section className="w-full rounded-lg border border-slate-200 bg-white p-6 shadow-xl shadow-slate-200/60 dark:border-slate-700 dark:bg-slate-800 dark:shadow-black/30 sm:p-8">
                    <div className="mb-8 flex items-center justify-between gap-4">
                        <div>
                            <div className="text-3xl font-bold tracking-normal">
                                <span className="text-pink-500">Meow</span>
                                <span className="text-indigo-600 dark:text-indigo-400">Search</span>
                            </div>
                            <div className="mt-1 text-xs font-medium uppercase text-slate-400">
                                Private access
                            </div>
                        </div>
                        {user && (
                            <img
                                src={getDiscordAvatarUrl(user)}
                                alt=""
                                className="h-11 w-11 rounded-lg bg-slate-200 object-cover dark:bg-slate-700"
                            />
                        )}
                    </div>

                    <div className="space-y-6">
                        <div>
                            <h1 className="text-2xl font-bold">
                                {accessApproved
                                    ? "Access approved"
                                    : request?.status === "pending"
                                    ? "Request pending"
                                    : request?.status === "denied"
                                        ? "Request denied"
                                        : "Request access"}
                            </h1>
                            <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">
                                {accessApproved &&
                                    "Sign in again to receive a search-enabled session."}
                                {!accessApproved && request?.status === "pending" &&
                                    "An administrator still needs to review your request."}
                                {!accessApproved && request?.status === "denied" &&
                                    "You may update your explanation and submit a new request."}
                                {!accessApproved && !request &&
                                    "Tell an administrator why you need access to the archive."}
                            </p>
                        </div>

                        {request?.request_text && (
                            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm dark:border-slate-700 dark:bg-slate-900">
                                <div className="mb-1 text-xs font-semibold uppercase text-slate-400">
                                    Your request
                                </div>
                                <div className="whitespace-pre-wrap text-slate-700 dark:text-slate-200">
                                    {request.request_text}
                                </div>
                            </div>
                        )}

                        {request?.review_note && (
                            <div className="rounded-lg border border-slate-200 p-4 text-sm dark:border-slate-700">
                                <div className="mb-1 text-xs font-semibold uppercase text-slate-400">
                                    Reviewer note
                                </div>
                                <div className="whitespace-pre-wrap">{request.review_note}</div>
                            </div>
                        )}

                        {canSubmit && (
                            <form onSubmit={submitRequest} className="space-y-3">
                                <label htmlFor="access-request" className="block text-sm font-semibold">
                                    Why do you need access?
                                </label>
                                <textarea
                                    id="access-request"
                                    value={requestText}
                                    onChange={(event) => setRequestText(event.target.value)}
                                    maxLength={1000}
                                    rows={5}
                                    required
                                    className="w-full resize-y rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 dark:border-slate-600 dark:bg-slate-900"
                                />
                                <div className="flex items-center justify-between gap-3">
                                    <span className="text-xs text-slate-400">{requestText.length}/1000</span>
                                    <button
                                        type="submit"
                                        disabled={loading || !requestText.trim()}
                                        className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
                                    >
                                        {loading ? "Submitting..." : "Submit request"}
                                    </button>
                                </div>
                            </form>
                        )}

                        {error && (
                            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/70 dark:bg-red-950/40 dark:text-red-300">
                                {error}
                            </div>
                        )}

                        <div className="flex flex-wrap gap-3 border-t border-slate-200 pt-5 dark:border-slate-700">
                            {accessApproved ? (
                                <button
                                    type="button"
                                    onClick={onSignInAgain}
                                    className="rounded-lg bg-[#5865F2] px-4 py-2 text-sm font-bold text-white hover:bg-[#4752C4]"
                                >
                                    Sign in again
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={checkStatus}
                                    disabled={loading}
                                    className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold hover:bg-slate-50 disabled:opacity-60 dark:border-slate-600 dark:hover:bg-slate-700"
                                >
                                    {loading ? "Checking..." : "Check status"}
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={onLogout}
                                className="px-3 py-2 text-sm font-semibold text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
                            >
                                Sign out
                            </button>
                        </div>
                    </div>
                </section>
            </main>
        </div>
    );
}

export default AccessRequestPanel;
